import React, { useEffect, useRef } from 'react'
import * as THREE from 'three'
// Use @types/three: import types from extensionless module specifiers
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader'
export type Snapshot3DProps = {
  /** GLB/GLTF file URL (GLB recommended) */
  modelUrl: string
  /** Rotation in degrees (XYZ) */
  rotationDeg?: { x: number; y: number; z: number }
  /** Model translation (optional) */
  position?: { x: number; y: number; z: number }
  /** Background color hex (e.g. 0x0d0d0d) */
  background?: number
  /** Styling/classes for page placement */
  className?: string
  style?: React.CSSProperties
  /** If true, enable interactive orbit controls (mouse/touch). */
  interactive?: boolean
}

// Theme-aware default backgrounds
const BG_DARK = 0x1b2635 // existing dark blue
const BG_LIGHT = 0xf7f9fc // requested light background

function getCurrentTheme(): 'light' | 'dark' {
  try {
    const root = document.documentElement
    const body = document.body

    // Prefer explicit app theme hints on html/body
    const getAttr = (el: HTMLElement | null, name: string) =>
      (el?.getAttribute?.(name) || '').toLowerCase()
    const dsHtml = (root.getAttribute('data-theme') || root.dataset?.theme || '').toLowerCase()
    const dsBody = (body?.getAttribute('data-theme') || body?.dataset?.theme || '').toLowerCase()
    const muiHtml = getAttr(root, 'data-mui-color-scheme')
    const muiBody = getAttr(body, 'data-mui-color-scheme')
    const genericHtml = getAttr(root, 'data-color-scheme')
    const genericBody = getAttr(body, 'data-color-scheme')

    const candidates = [dsHtml, dsBody, muiHtml, muiBody, genericHtml, genericBody]
    for (const val of candidates) {
      if (val === 'dark' || val === 'light') return val as 'dark' | 'light'
    }

    // Class-based hints
    if (root.classList.contains('dark') || body?.classList.contains('dark')) return 'dark'
    if (root.classList.contains('light') || body?.classList.contains('light')) return 'light'

    // Fallback: OS preference
    const mm = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)')
    return mm && mm.matches ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

const Snapshot3D: React.FC<Snapshot3DProps> = ({
  modelUrl,
  rotationDeg = { x: 0, y: 0, z: 0 },
  position,
  // If not provided, background auto-follows theme (dark: #1b2635, light: #f7f9fc)
  background,
  className,
  style,
  interactive = false
}) => {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null)
  const sceneRef = useRef<THREE.Scene | null>(null)
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null)
  const modelRef = useRef<THREE.Object3D | null>(null)
  type OrbitControlsLike = {
    dispose: () => void
    update: () => void
    minPolarAngle: number
    maxPolarAngle: number
    getPolarAngle?: () => number
  }
  const controlsRef = useRef<OrbitControlsLike | null>(null)
  const animRef = useRef<number | null>(null)

  // Resolve background: prop overrides theme; otherwise pick based on current theme
  const [resolvedBg, setResolvedBg] = React.useState<number>(() =>
    typeof background === 'number' ? background : getCurrentTheme() === 'dark' ? BG_DARK : BG_LIGHT
  )

  // Keep resolvedBg in sync with prop and theme changes
  useEffect(() => {
    if (typeof background === 'number') {
      setResolvedBg(background)
      return
    }

    const applyTheme = () => {
      const theme = getCurrentTheme()
      setResolvedBg(theme === 'dark' ? BG_DARK : BG_LIGHT)
    }

    applyTheme()

    // Observe data-theme/class changes on <html>
    const root = document.documentElement
    const body = document.body
    const mo = new MutationObserver(applyTheme)
    try {
      const attrs = ['class', 'data-theme', 'data-mui-color-scheme', 'data-color-scheme']
      mo.observe(root, { attributes: true, attributeFilter: attrs })
      if (body) mo.observe(body, { attributes: true, attributeFilter: attrs })
    } catch {
      /* noop */
    }

    // Listen to OS color scheme changes
    let mm: MediaQueryList | null = null
    let mqlHandler: ((e: MediaQueryListEvent) => void) | null = null
    try {
      mm = window.matchMedia('(prefers-color-scheme: dark)')
      mqlHandler = () => applyTheme()
      if (typeof mm.addEventListener === 'function') mm.addEventListener('change', mqlHandler)
      else if (
        typeof (mm as unknown as { addListener?: (cb: (e: MediaQueryListEvent) => void) => void })
          .addListener === 'function'
      )
        (
          mm as unknown as { addListener: (cb: (e: MediaQueryListEvent) => void) => void }
        ).addListener(mqlHandler)
    } catch {
      /* noop */
    }

    return () => {
      mo.disconnect()
      if (mm && mqlHandler) {
        try {
          if (typeof mm.removeEventListener === 'function')
            mm.removeEventListener('change', mqlHandler)
          else if (
            typeof (
              mm as unknown as { removeListener?: (cb: (e: MediaQueryListEvent) => void) => void }
            ).removeListener === 'function'
          )
            (
              mm as unknown as { removeListener: (cb: (e: MediaQueryListEvent) => void) => void }
            ).removeListener(mqlHandler)
        } catch {
          /* noop */
        }
      }
    }
  }, [background])

  // Luo perus Three-objektit kerran
  useEffect(() => {
    const host = hostRef.current!
    const scene = new THREE.Scene()
    scene.background = new THREE.Color(resolvedBg)

    const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 1000)

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      preserveDrawingBuffer: true // keep one rendered frame on the canvas
    })

    rendererRef.current = renderer
    sceneRef.current = scene
    cameraRef.current = camera

    // Ensure host clips overflow so canvas cannot overlap neighboring UI
    host.style.position = host.style.position || 'relative'
    host.style.overflow = 'hidden'

    // Make sure the canvas fills the host and is positioned inside it
    renderer.domElement.style.position = 'absolute'
    renderer.domElement.style.top = '0'
    renderer.domElement.style.left = '0'
    renderer.domElement.style.width = '100%'
    renderer.domElement.style.height = '100%'
    renderer.domElement.style.display = 'block'
    renderer.domElement.style.touchAction = 'none'
    renderer.domElement.style.pointerEvents = 'auto'

    host.appendChild(renderer.domElement)

    // Light-weight lighting setup
    scene.add(new THREE.AmbientLight(0xffffff, 0.7))
    const dir = new THREE.DirectionalLight(0xffffff, 1.0)
    dir.position.set(3, 5, 2)
    scene.add(dir)

    // Cleanup on unmount
    return () => {
      if (rendererRef.current) {
        rendererRef.current.dispose()
        if (rendererRef.current.domElement.parentNode) {
          rendererRef.current.domElement.parentNode.removeChild(rendererRef.current.domElement)
        }
      }
      sceneRef.current = null
      cameraRef.current = null
      modelRef.current = null
    }
  }, [])

  // Apply background updates to scene without reloading model
  useEffect(() => {
    const scene = sceneRef.current
    const renderer = rendererRef.current
    const camera = cameraRef.current
    if (!scene || !renderer || !camera) return
    scene.background = new THREE.Color(resolvedBg)
    // Re-render a frame in snapshot mode; interactive mode renders continuously
    if (!interactive) {
      try {
        renderer.render(scene, camera)
      } catch {
        /* ignore */
      }
    }
  }, [resolvedBg, interactive])

  // Load model and render a frame whenever modelUrl/rotation/position changes
  useEffect(() => {
    let cancelled = false
    const host = hostRef.current
    const scene = sceneRef.current
    const camera = cameraRef.current
    const renderer = rendererRef.current

    if (!host || !scene || !camera || !renderer) return

    // Set renderer size to container dimensions
    const fitRenderer = () => {
      // prefer the actual canvas display size if set by CSS
      const canvas = renderer.domElement
      const w = canvas.clientWidth || host.clientWidth || 1
      const h = canvas.clientHeight || host.clientHeight || 1
      camera.aspect = w / h
      camera.updateProjectionMatrix()
      renderer.setSize(w, h, false)
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    }
    fitRenderer()

    const handleResize = () => fitRenderer()
    window.addEventListener('resize', handleResize)

    // Remove old model
    if (modelRef.current) {
      scene.remove(modelRef.current)
      modelRef.current = null
    }

    // Dynamic import for loader (smaller initial bundle)
    ;(async () => {
      const lower = modelUrl.toLowerCase()
      let root: THREE.Object3D | null = null

      if (lower.endsWith('.glb') || lower.endsWith('.gltf')) {
        const { GLTFLoader } = (await import(
          'three/examples/jsm/loaders/GLTFLoader'
        )) as typeof import('three/examples/jsm/loaders/GLTFLoader')
        root = await new Promise<THREE.Object3D>((resolve, reject) => {
          new GLTFLoader().load(modelUrl, (gltf: GLTF) => resolve(gltf.scene), undefined, reject)
        })
      } else {
        throw new Error(
          'Suositus on GLB/GLTF. (OBJ onnistuu lisätyllä OBJLoaderilla, mutta GLB on kevyempi.)'
        )
      }

      if (cancelled) return

      // Apply rotation & optional position
      root.rotation.set(
        THREE.MathUtils.degToRad(rotationDeg.x),
        THREE.MathUtils.degToRad(rotationDeg.y),
        THREE.MathUtils.degToRad(rotationDeg.z),
        'XYZ'
      )
      if (position) root.position.set(position.x, position.y, position.z)

      // Ensure materials exist if missing
      root.traverse((obj: THREE.Object3D) => {
        const mesh = obj as THREE.Mesh
        if ((mesh as unknown as { isMesh?: boolean }).isMesh) {
          if (!mesh.material) {
            mesh.material = new THREE.MeshStandardMaterial({ color: 0xcccccc })
          }
          mesh.castShadow = false
          mesh.receiveShadow = false
        }
      })

      scene.add(root)
      modelRef.current = root

      // Fit camera to model in its current pose
      const box = new THREE.Box3().setFromObject(root)
      const size = box.getSize(new THREE.Vector3())
      const center = box.getCenter(new THREE.Vector3())

      const radius = size.length() * 0.6 + 0.001
      const halfFov = THREE.MathUtils.degToRad(camera.fov / 2)
      const dist = radius / Math.tan(halfFov)

      camera.near = Math.max(0.01, dist / 1000)
      camera.far = dist * 10
      camera.position.copy(center).add(new THREE.Vector3(dist, dist * 0.25, dist))
      camera.lookAt(center)
      camera.updateProjectionMatrix()

      if (interactive) {
        // Enable orbit controls and continuous rendering for interactivity
        ;(async () => {
          try {
            const { OrbitControls } = (await import(
              'three/examples/jsm/controls/OrbitControls'
            )) as typeof import('three/examples/jsm/controls/OrbitControls')
            // debug: confirm controls loaded

            console.debug('OrbitControls loaded')
            const controls = new OrbitControls(camera, renderer.domElement)
            controls.enablePan = false
            controls.enableDamping = true
            controls.dampingFactor = 0.07
            controls.target.copy(center)
            controls.update()
            // Lock vertical movement (polar angle) to current pitch so user can only rotate around Y axis
            try {
              const polar =
                typeof controls.getPolarAngle === 'function'
                  ? controls.getPolarAngle()
                  : Math.PI / 2
              controls.minPolarAngle = polar
              controls.maxPolarAngle = polar
            } catch (_err) {
              // If any error, fall back to locking at 90 degrees
              controls.minPolarAngle = Math.PI / 2
              controls.maxPolarAngle = Math.PI / 2
            }
            controlsRef.current = controls

            const animate = () => {
              controls.update()
              renderer.render(scene, camera)
              animRef.current = requestAnimationFrame(animate)
            }

            animate()
          } catch (err) {
            console.error('Failed to load OrbitControls', err)
            renderer.render(scene, camera)
          }
        })()
      } else {
        // Render a single frame for snapshot mode
        renderer.render(scene, camera)
      }
    })().catch(console.error)

    return () => {
      cancelled = true
      if (animRef.current) {
        cancelAnimationFrame(animRef.current)
        animRef.current = null
      }
      if (controlsRef.current) {
        try {
          controlsRef.current.dispose()
        } catch (_e) {
          // ignore
        }
        controlsRef.current = null
      }
      window.removeEventListener('resize', handleResize)
    }
    // Note: background changes are handled separately to avoid reloading the model
  }, [modelUrl, rotationDeg.x, rotationDeg.y, rotationDeg.z, position?.x, position?.y, position?.z])

  return (
    <div
      ref={hostRef}
      className={className}
      style={{
        // Default to fill parent's size so parent components can control sizing responsively
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...style
      }}
    />
  )
}

export default Snapshot3D
